import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
  HStack,
  ScaleFade,
  SlideFade,
  useBreakpointValue,
  Grid,
  GridItem,
  SimpleGrid,
  Badge,
  Icon,
  List,
  ListItem,
  ListIcon,
  Divider,
  chakra,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import { COLORS, GRADIENTS, ROUTES } from '../utils/constants';
import useAuth from '../store/useAuth';
import { 
  FaCheck, 
  FaChevronRight, 
  FaStar, 
  FaRocket, 
  FaShieldAlt, 
  FaChartLine, 
  FaCoins, 
  FaImage, 
  FaArrowRight,
  FaWallet,
  FaTrophy,
  FaBrain,
  FaLock,
  FaUserFriends,
  FaExchangeAlt,
  FaLaughSquint,
  FaFileAlt,
  FaCertificate,
  FaChartBar,
  FaTwitter,
  FaTicketAlt,
  FaVideo,
  FaRobot,
  FaChevronDown,
  FaQuestionCircle,
} from 'react-icons/fa';
import EmojiSpiral from '../components/EmojiSpiral';
import DigitalText from '../components/DigitalText';
import { motion, AnimatePresence } from 'framer-motion';


// Hexagon component for visual identity
const MotionBox = motion(Box);
const Hexagon = ({ size, position, delay, opacity, color, rotationSpeed }) => (
  <Box
    position="absolute"
    width={size}
    height={size}
    {...position}
    opacity={opacity}
    zIndex="0"
    className="hexagon-animation"
    sx={{
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'transparent',
        border: `1px solid ${color || COLORS.primary}`,
        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
        animation: `rotate ${rotationSpeed || 60}s linear infinite`,
        animationDelay: `${delay || 0}s`,
        opacity: 0.6
      },
      '@keyframes rotate': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
      }
    }}
  />
);

// Feature Card component - enhanced styling with better visual hierarchy
const FeatureCard = ({ title, icon, description }) => (
  <Box
    sx={{
      '--border-top-width': '0.5px',
      '--border-right-width': '0.5px',
      '--border-bottom-width': '0.5px',
      '--border-left-width': '0.5px',
      '--border-style': 'dashed',
      '--border-color': 'rgb(7, 74, 0)',
      'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
      'borderStyle': 'var(--border-style)',
      'borderColor': 'var(--border-color)'
    }}
    borderRadius="20px"
    overflow="hidden"
    bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
    p={6}
    h="100%"
    transition="all 0.3s ease-in-out"
    boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
    _hover={{
      transform: 'translateY(-8px)',
      boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
      borderColor: 'rgba(32, 230, 13, 0.6)'
    }}
    className="glow-border"
    position="relative"
  >
    {/* Icon container with enhanced styling */}
    <Box
      mb={5}
      p={3}
      borderRadius="12px"
      bg="rgba(30, 220, 11, 0.1)"
      border="1px solid rgba(30, 220, 11, 0.2)"
      w="fit-content"
      transition="all 0.3s"
      _groupHover={{
        bg: "rgba(30, 220, 11, 0.15)",
        transform: "scale(1.05)"
      }}
    >
      <Icon 
        as={icon} 
        color={COLORS.primary} 
        boxSize={10}
        filter="drop-shadow(0 0 8px rgba(30, 220, 11, 0.3))"
      />
    </Box>
    
    <VStack spacing={0} align="flex-start" flex="1">
      <Heading 
        size="lg" 
        color="white"
        fontWeight="bold"
        letterSpacing="tight"
        lineHeight="1.2"
        mb={3}
      >
        {title}
      </Heading>
      <Text 
        color="rgba(255, 255, 255, 0.85)"
        fontSize="md"
        lineHeight="1.6"
        flex="1"
      >
        {description}
      </Text>
    </VStack>
    
    {/* Subtle gradient overlay for depth */}
    <Box
      position="absolute"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="linear-gradient(135deg, rgba(30, 220, 11, 0.02) 0%, transparent 50%, rgba(30, 220, 11, 0.02) 100%)"
      borderRadius="20px"
      pointerEvents="none"
    />
  </Box>
);

// Animated GlowBadge component
const GlowBadge = ({ children }) => (
  <Badge
    px={4} 
    py={2}
    borderRadius="full"
    fontSize="md"
    bg="rgba(0, 168, 107, 0.15)"
    color={COLORS.primary}
    className="pulse-animation"
    sx={{
      '@keyframes pulse': {
        '0%': { boxShadow: '0 0 8px rgba(0, 207, 51, 0.3)' },
        '50%': { boxShadow: '0 0 15px rgba(0, 207, 51, 0.5)' },
        '100%': { boxShadow: '0 0 8px rgba(0, 207, 51, 0.3)' }
      },
      animation: 'pulse 3s infinite'
    }}
  >
    {children}
  </Badge>
);

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <Box 
    borderBottom="1px solid rgba(255, 255, 255, 0.1)"
    py={6}
    w="100%"
    maxW="100%"
    transition="all 0.3s ease"
    _hover={{
      bg: "rgba(30, 220, 11, 0.02)"
    }}
    overflow="hidden"
  >
    <Button
      variant="unstyled"
      onClick={onToggle}
      w="100%"
      maxW="100%"
      h="auto"
      p={0}
      justifyContent="space-between"
      display="flex"
      alignItems="flex-start"
      textAlign="left"
      _hover={{ bg: "transparent" }}
      overflow="hidden"
    >
      <Text 
        color="white" 
        fontSize={{ base: "sm", md: "md", lg: "lg" }}
        fontWeight="semibold" 
        textAlign="left"
        flex="1"
        pr={{ base: 2, md: 4 }}
        wordBreak="break-word"
        overflowWrap="anywhere"
        lineHeight="1.4"
        maxW="calc(100% - 32px)"
      >
        {question}
      </Text>
      <Icon 
        as={FaChevronDown} 
        color="rgba(255, 255, 255, 0.6)"
        boxSize={{ base: 4, md: 5 }}
        transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
        transition="transform 0.3s ease"
        flexShrink={0}
        mt={1}
        ml={2}
      />
    </Button>
    
    <Collapse in={isOpen} animateOpacity>
      <Box pt={4} pb={2} maxW="100%" overflow="hidden">
        <Text 
          color="rgba(255, 255, 255, 0.7)" 
          fontSize={{ base: "xs", md: "sm", lg: "md" }}
          lineHeight="1.7"
          wordBreak="break-word"
          overflowWrap="anywhere"
          maxW="100%"
        >
          {answer}
        </Text>
      </Box>
    </Collapse>
  </Box>
);

function Home() {
  const [isVisible, setIsVisible] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [stats, setStats] = useState({ users: 0, images: 0, rating: 0 });
  
  // Responsive adjustments
  const buttonSize = useBreakpointValue({ base: 'md', md: 'lg' });
  const headingSize = useBreakpointValue({ base: '3xl', md: '4xl', lg: '5xl' });
  const textSize = useBreakpointValue({ base: 'md', md: 'lg' });
  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3 });
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
    
    // Animate stats with counting effect
    const timer = setTimeout(() => {
      let count = 0;
      const interval = setInterval(() => {
        count += 5;
        setStats({
          users: Math.min(count * 5, 2400),
          images: Math.min(count * 170, 85000),
          rating: Math.min(count * 0.05, 5.0)
        });
        
        if (count >= 100) clearInterval(interval);
      }, 40);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // FAQ data
  const faqData = [
    {
      id: 1,
      question: "What is Ignita?",
      answer: "Ignita is the first AI image generator powered by the XRP Ledger. We provide cutting-edge AI tools for creating stunning visuals, digital art, and branded content in seconds. Built for speed, security, and scalability using $XGCL tokens at the core, Ignita is part of the XGC Labs ecosystem designed for creators and fueled by utility."
    },
    {
      id: 2,
      question: "What is $XGCL?",
      answer: "$XGCL is the native utility token that powers the Ignita platform and the broader XGC Labs ecosystem. It enables fast, affordable transactions for AI image generation, provides access to premium features, and offers opportunities to earn rewards through platform participation and community engagement."
    },
    {
      id: 3,
      question: "How do I start generating images?",
      answer: "Getting started is simple! First, create an account and connect your XRPL wallet. Once you have $XGCL tokens in your wallet, navigate to the image generation tool, enter your creative prompt, select your preferred style and settings, and click generate. Your high-quality AI image will be ready in 10-15 seconds."
    },

    {
      id: 4,
      question: "Can I generate AI videos on Ignita?",
      answer: "Yes! Ignita features powerful AI video generation capabilities that allow you to create custom videos with ease. Simply define your style, duration, and resolution preferences, and our advanced AI handles the rest, delivering polished video content from concept to final cut. Experience the future of video creation with blockchain-powered efficiency."
    },
    {
      id: 5,
      question: "What is the NFT Maker feature?",
      answer: "The NFT Maker is an upcoming feature that will allow you to seamlessly transform your AI-generated creations into unique NFTs with just a few clicks. This tool will enable creators to mint, showcase, and trade their digital art directly on the blockchain. Stay tuned – the NFT Maker is coming soon to the Ignita platform!"
    }
  ];

  const handleFAQToggle = (faqId) => {
    setOpenFAQ(openFAQ === faqId ? null : faqId);
  };
  
  const handleGetStarted = (e) => {
    e.preventDefault(); // Prevent any default behavior
    if (isLoggedIn) {
      navigate('/image');
    } else {
      navigate('/login');
    }
  };
  
  return (
    <Box overflow="hidden" position="relative">
      {/* Strategic hexagon decorations */}
      {/* <Hexagon size="350px" position={{ top: "-50px", left: "-150px" }} opacity={0.15} rotationSpeed={120} />
      <Hexagon size="250px" position={{ bottom: "20%", right: "-100px" }} opacity={0.1} delay={2} />
      <Hexagon size="180px" position={{ top: "40%", left: "10%" }} opacity={0.08} rotationSpeed={90} delay={4} />
      <Hexagon size="200px" position={{ bottom: "10%", left: "20%" }} opacity={0.1} delay={1} /> */}
      
      {/* Add Emoji Spiral Animation */}
      <EmojiSpiral />
      
      <Container maxW="7xl" py={8} px={{ base: 4, md: 8 }}>
        <VStack spacing={12} align="stretch">
          {/* Main hero section */}
          <Box textAlign="center" py={{ base: 10, md: 20 }}>
            {/* Social media icons */}
            {/* <HStack spacing={2} justify="center" mb={10}>
              <Box 
                bg="rgba(30, 30, 30, 0.8)" 
                borderRadius="md" 
                p={2}
                boxShadow="0px 0px 5px rgba(0, 0, 0, 0.5)"
              >
                <Icon as={FaTwitter} color="gray.400" boxSize={5} />
              </Box>
              <Box 
                bg="rgba(30, 30, 30, 0.8)" 
                borderRadius="md" 
                p={2}
                boxShadow="0px 0px 5px rgba(0, 0, 0, 0.5)"
              >
                <Icon as={FaImage} color="gray.400" boxSize={5} />
              </Box>
              <Box 
                bg="rgba(30, 30, 30, 0.8)" 
                borderRadius="md" 
                p={2}
                boxShadow="0px 0px 5px rgba(0, 0, 0, 0.5)"
              >
                <Icon as={FaLock} color="gray.400" boxSize={5} />
              </Box>
            </HStack> */}
            
            {/* Digital text effect for main heading */}
            <Box className="lcd-screen">
              <Text className="segment-lcd">
                IGNITA – Next-Gen AI Powered by XGCL
              </Text>
            </Box>
            
            <Text
              fontSize="md"
              color="white"
              maxW="3xl"
              mx="auto"
              mt={6}
              mb={8}
              margin="auto"
            >
              Step into the future of creativity with IGNITA, the first AI image generator built on the XRP Ledger. Instantly generate breathtaking visuals, digital artwork, and branded content with unmatched speed, security, and scalability.
              <br/><br/>
Driven by $XGCL and developed by XGC Labs, IGNITA is more than a tool, it's a creative revolution.
Built for creators. Powered by utility. Engineered for impact.
            </Text>
            
            <Box py={8}>
              <Flex justify="center">
                <Button
                  className="primary-button"
                  onClick={handleGetStarted}
                  rightIcon={<FaTicketAlt />}
                  px={10}
                  width="240px"
                  style={{
                    fontSize: "20px", fontWeight: "700", padding: "25px", width: "300px"}}
                >
                  Start Creating Now
                </Button>
              </Flex>
            </Box>
            

          </Box>



          {/* What is Ignita Section */}
          <Box py={12} paddingTop={"0"}>
            <Heading
              as="h2"
              size="xl"
              mb={4}
              textAlign="center"
              color="white"
              fontWeight="bold"
            >
              Welcome to<br/>The IGNITA Platform
            </Heading>
            <Text
              textAlign="center"
              color="rgba(255, 255, 255, 0.8)"
              fontSize="md"
              mb={10}
              maxW="4xl"
              mx="auto"
            >
              Unleash your creative potential with our powerful suite of AI-driven tools, designed to turn ideas into stunning visuals in seconds.
            </Text>
            <Flex 
              direction="row"
              wrap="wrap" 
              justify="center" 
              gap={8}
              align="stretch"
            >
              <Box 
                w={{ base: "100%", md: "calc(50% - 16px)", lg: "calc(33.333% - 16px)" }}
                maxW="420px"
                minW="320px"
                role="group"
              >
                <FeatureCard 
                  title="AI Image Generation"
                  icon={FaImage}
                  description="Transform your ideas into stunning, high-quality visuals in seconds. Our cutting-edge AI understands context, style, and detail, turning any prompt into artwork that captivates."
                />
              </Box>
              <Box 
                w={{ base: "100%", md: "calc(50% - 16px)", lg: "calc(33.333% - 16px)" }}
                maxW="420px"
                minW="320px"
                role="group"
              >
                <FeatureCard 
                  title="AI Video Generation"
                  icon={FaVideo}
                  description="Produce custom videos with ease. Define your style, duration, and resolution, our AI takes care of the rest, delivering polished content from concept to final cut."
                />
              </Box>
              <Box 
                w={{ base: "100%", md: "calc(50% - 16px)", lg: "calc(33.333% - 16px)" }}
                maxW="420px"
                minW="320px"
                role="group"
              >
                <FeatureCard 
                  title="AI Assistant"
                  icon={FaRobot}
                  description="Need a hand? Get instant support, creative ideas, or technical guidance from our smart AI, your personal co-pilot for all things content and creation."
                />
              </Box>
              <Box 
                w={{ base: "100%", md: "calc(50% - 16px)", lg: "calc(33.333% - 16px)" }}
                maxW="420px"
                minW="320px"
                role="group"
                position="relative"
              >
                <FeatureCard 
                  title="NFT Maker"
                  icon={FaImage}
                  description="Coming soon, a seamless tool to transform your creations into unique NFTs in just a few clicks."
                />
                <MotionBox
                  position="absolute"
                  top={{ base: 3, md: 4 }}
                  right={{ base: 3, md: 4 }}
                  zIndex={10}
                  animate={{ 
                    y: [-5, 5, -5],
                    rotate: [-5, 5, -5]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Badge
                    colorScheme="green"
                    variant="solid"
                    px={{ base: 2, md: 3 }}
                    py={{ base: 1, md: 1.5 }}
                    borderRadius="full"
                    fontSize={{ base: 'xs', md: 'sm' }}
                    fontWeight="bold"
                    bg={COLORS.primary}
                    color={COLORS.background}
                    boxShadow={`0 0 30px rgba(30, 220, 11, 0.6)`}
                  >
                    COMING SOON
                  </Badge>
                </MotionBox>
              </Box>
            </Flex>
          </Box>

          {/* Key Features Section */}
          <Box py={8} bg="rgba(10, 24, 10, 0.7)" borderRadius="lg" p={8}>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={8}>
              <Box>
                <List spacing={4}>
                  <ListItem>
                    <HStack alignItems={"start"}>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md"> High-resolution, premium image generation
Craft stunning visuals with crystal-clear detail, powered by advanced AI.</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack  alignItems={"start"}>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md"> AI video generation with full customization
Select duration, resolution, and style — let the AI handle the rest.</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack  alignItems={"start"}>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md">Smart AI Assistant
Get real-time creative help, suggestions, and technical support.</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack  alignItems={"start"}>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md">Diverse artistic styles & aesthetics
Choose from multiple art styles to match your vision.
</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack  alignItems={"start"}>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md">Full commercial usage rights
Everything you generate is yours to use, sell, or monetize.</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack  alignItems={"start"}>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md"> Secure wallet integration
Seamlessly connect your wallet for smooth XGCL transactions</Text>
                    </HStack>
                  </ListItem>
                </List>
              </Box>
              <Box 
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                h="100%"
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                height={"max-content"}
                className="glow-border"
                position="relative"
              >            <VStack spacing={0} align="stretch">
              <Heading size="md" color="white" mb={3}>Platform Benefits</Heading>
                  <HStack justify="space-between">
                    <Text color="white">Fast Generation</Text>
                    <Badge colorScheme="green">15-60 Seconds</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="white">Ownership Verification</Text>
                    <Badge colorScheme="green">Blockchain Secured</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="white">Style Options</Text>
                    <Badge colorScheme="green">10+ Styles</Badge>
                  </HStack>
                  <Divider borderColor="rgba(0, 168, 107, 0.3)" />
                  <HStack justify="space-between">
                    <Text color="white" fontWeight="bold">Commercial Usage</Text>
                    <Badge colorScheme="green" variant="solid">Included</Badge>
                  </HStack>
                </VStack>
              </Box>
            </Grid>
          </Box>



          

          {/* XGCL Token Integration Section */}
          <Box py={8} bg="rgba(10, 24, 10, 0.7)" borderRadius="lg" p={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              textAlign="center"
              color="white"
            >
              XGCL Token Integration
            </Heading>
            <Grid templateColumns={{ base: '1fr', md: '1fr' }} gap={8} alignItems="center">
              <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                textAlign="center"
              >
                <Icon as={FaWallet} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color="white" mb={3}>XGCL Token Powered</Heading>
                <Text fontSize="md" color={COLORS.textSecondary} mb={6}>
                  Use XGCL tokens for fast and affordable AI image generation with different quality tiers and options.
                </Text>
              </Box>
              
              {/* <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                textAlign="center"
              >
                <Icon as={FaExchangeAlt} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color="white" mb={3}>Multiple Image Styles</Heading>
                <Text color="white">
                  Choose from a wide range of artistic styles, visual aesthetics, and rendering options for your AI creations.
                </Text>
              </Box> */}
              
              <Box
                borderWidth="1px"
                borderColor={COLORS.primary}
                borderRadius="lg"
                p={5}
                className="glow-border"
                bg="rgba(0, 0, 0, 0.6)"
                textAlign="center"
                display="none"
              >
                <Icon as={FaCoins} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color="white" mb={3}>XGCL Rewards Program</Heading>
                <Text fontSize="md" color={COLORS.textSecondary} mb={6}>
                  Earn XGCL tokens by participating in community challenges, winning image contests, and contributing to the platform.
                </Text>
              </Box>
            </Grid>
          </Box>


          {/* Competitions & Templates Section */}
          {/* <Box py={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={4}
              textAlign="center"
              color="white"
              fontWeight="bold"
            >
              Community & Opportunities
            </Heading>
            <Text
              textAlign="center"
              color="rgba(255, 255, 255, 0.8)"
              fontSize="lg"
              mb={10}
              maxW="3xl"
              mx="auto"
            >
              Join our thriving community and discover new ways to showcase your creativity
            </Text>
            <Flex 
              direction="row"
              wrap="wrap" 
              justify="center" 
              gap={8}
              align="stretch"
            >
              <Box 
                w={{ base: "100%", md: "calc(50% - 16px)", lg: "400px" }}
                maxW="450px"
                role="group"
              >
                <Box
                  sx={{
                    '--border-top-width': '0.5px',
                    '--border-right-width': '0.5px',
                    '--border-bottom-width': '0.5px',
                    '--border-left-width': '0.5px',
                    '--border-style': 'dashed',
                    '--border-color': 'rgb(7, 74, 0)',
                    'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                    'borderStyle': 'var(--border-style)',
                    'borderColor': 'var(--border-color)'
                  }}
                  borderRadius="20px"
                  overflow="hidden"
                  bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                  p={6}
                  h="100%"
                  transition="all 0.3s ease-in-out"
                  boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                  _hover={{
                    transform: 'translateY(-8px)',
                    boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                    borderColor: 'rgba(32, 230, 13, 0.6)'
                  }}
                  className="glow-border"
                  position="relative"
                >
                 
                  <Box
                    mb={5}
                    p={3}
                    borderRadius="12px"
                    bg="rgba(30, 220, 11, 0.1)"
                    border="1px solid rgba(30, 220, 11, 0.2)"
                    w="fit-content"
                    transition="all 0.3s"
                    _groupHover={{
                      bg: "rgba(30, 220, 11, 0.15)",
                      transform: "scale(1.05)"
                    }}
                  >
                    <Icon 
                      as={FaTrophy} 
                      color={COLORS.primary} 
                      boxSize={10}
                      filter="drop-shadow(0 0 8px rgba(30, 220, 11, 0.3))"
                    />
                  </Box>
                  
                  <VStack spacing={0} align="flex-start" flex="1">
                    <Heading 
                      size="lg" 
                      color="white"
                      fontWeight="bold"
                      letterSpacing="tight"
                      lineHeight="1.2"
                      mb={3}
                    >
                      Weekly Contests
                    </Heading>
                    <Text 
                      color="rgba(255, 255, 255, 0.85)"
                      fontSize="md"
                      lineHeight="1.6"
                      flex="1"
                    >
                      Join weekly themed competitions and showcase your creativity with the community.
                    </Text>
                  </VStack>
                  
                  
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="linear-gradient(135deg, rgba(30, 220, 11, 0.02) 0%, transparent 50%, rgba(30, 220, 11, 0.02) 100%)"
                    borderRadius="20px"
                    pointerEvents="none"
                  />
                </Box>
              </Box>
              
              <Box 
                w={{ base: "100%", md: "calc(50% - 16px)", lg: "400px" }}
                maxW="450px"
                role="group"
              >
                <Box
                  sx={{
                    '--border-top-width': '0.5px',
                    '--border-right-width': '0.5px',
                    '--border-bottom-width': '0.5px',
                    '--border-left-width': '0.5px',
                    '--border-style': 'dashed',
                    '--border-color': 'rgb(7, 74, 0)',
                    'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                    'borderStyle': 'var(--border-style)',
                    'borderColor': 'var(--border-color)'
                  }}
                  borderRadius="20px"
                  overflow="hidden"
                  bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                  p={6}
                  h="100%"
                  transition="all 0.3s ease-in-out"
                  boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                  _hover={{
                    transform: 'translateY(-8px)',
                    boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                    borderColor: 'rgba(32, 230, 13, 0.6)'
                  }}
                  className="glow-border"
                  position="relative"
                >
                 
                  <Box
                    mb={5}
                    p={3}
                    borderRadius="12px"
                    bg="rgba(30, 220, 11, 0.1)"
                    border="1px solid rgba(30, 220, 11, 0.2)"
                    w="fit-content"
                    transition="all 0.3s"
                    _groupHover={{
                      bg: "rgba(30, 220, 11, 0.15)",
                      transform: "scale(1.05)"
                    }}
                  >
                    <Icon 
                      as={FaCoins} 
                      color={COLORS.primary} 
                      boxSize={10}
                      filter="drop-shadow(0 0 8px rgba(30, 220, 11, 0.3))"
                    />
                  </Box>
                  
                  <VStack spacing={0} align="flex-start" flex="1">
                    <Heading 
                      size="lg" 
                      color="white"
                      fontWeight="bold"
                      letterSpacing="tight"
                      lineHeight="1.2"
                      mb={3}
                    >
                      Earn Rewards
                    </Heading>
                    <Text 
                      color="rgba(255, 255, 255, 0.85)"
                      fontSize="md"
                      lineHeight="1.6"
                      flex="1"
                    >
                      Discover new monetization opportunities and reward programs within the platform.
                    </Text>
                  </VStack>
                  
                  
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="linear-gradient(135deg, rgba(30, 220, 11, 0.02) 0%, transparent 50%, rgba(30, 220, 11, 0.02) 100%)"
                    borderRadius="20px"
                    pointerEvents="none"
                  />
                </Box>
              </Box>
            </Flex>
          </Box> */}

          {/* Meme Creation Section - Renamed to AI Generation Tools */}
          {/* <Box py={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={4}
              textAlign="center"
              color="white"
            >
              AI Generation Tools
            </Heading>
            <Grid templateColumns={`repeat(${columns}, 1fr)`} gap={6}>
              <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                h="100%"
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                display="flex"
                flexDirection="column"
              >
                <Flex 
                  justify="center" 
                  align="center" 
                  bg="rgba(0, 0, 0, 0.3)" 
                  borderRadius="lg" 
                  h="180px" 
                  mb={4}
                >
                  <Icon as={FaRocket} color={COLORS.primary} boxSize="80px" opacity={0.8} />
                </Flex>
                <Heading size="md" color="white" mb={3}>AI Content Creation</Heading>
                <Text color="white" flex="1" mb={4}>
                  Generate professional creative content with customizable templates and industry-specific prompts
                </Text>
                <Button 
                  className="primary-button"
                  leftIcon={<FaRocket />}
                  onClick={() => navigate(ROUTES.PROMOTIONAL)}
                  alignSelf="flex-start"
                >
                  Create Content
                </Button>
              </Box>
              
              <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                h="100%"
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                display="flex"
                flexDirection="column"
              >
                <Flex 
                  justify="center" 
                  align="center" 
                  bg="rgba(0, 0, 0, 0.3)" 
                  borderRadius="lg" 
                  h="180px" 
                  mb={4}
                >
                  <Icon as={FaImage} color={COLORS.primary} boxSize="80px" opacity={0.8} />
                </Flex>
                <Heading size="md" color="white" mb={3}>High-Quality Generation</Heading>
                <Text color="white" flex="1" mb={4}>
                  Create stunning high-resolution images with incredible detail and clarity for business or personal use
                </Text>
                <Button 
                  className="primary-button"
                  leftIcon={<FaImage />}
                  onClick={() => navigate(ROUTES.PROMOTIONAL)}
                  alignSelf="flex-start"
                >
                  Generate HD Images
                </Button>
              </Box>
              
              <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                h="100%"
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                display="flex"
                flexDirection="column"
              >
                <Flex 
                  justify="center" 
                  align="center" 
                  bg="rgba(0, 0, 0, 0.3)" 
                  borderRadius="lg" 
                  h="180px" 
                  mb={4}
                >
                  <Icon as={FaChartBar} color={COLORS.primary} boxSize="80px" opacity={0.8} />
                </Flex>
                <Heading size="md" color="white" mb={3}>Business Visualizations</Heading>
                <Text color="white" flex="1" mb={4}>
                  Create professional data visualizations and business graphics optimized for the financial and crypto sectors
                </Text>
                <Button 
                  className="primary-button"
                  leftIcon={<FaChartBar />}
                  onClick={() => navigate(ROUTES.PROMOTIONAL)}
                  alignSelf="flex-start"
                >
                  Create Visuals
                </Button>
              </Box>
            </Grid>
          </Box> */}

          {/* Advanced AI Features Section */}
          <Box py={8} bg="rgba(10, 24, 10, 0.7)" borderRadius="lg" p={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              textAlign="center"
              color="white"
            >
              Advanced<br/>AI Features
            </Heading>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={8}>
              <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                textAlign="center"
              >
                <Icon as={FaBrain} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color="white" mb={3}>Smart Prompt Analysis</Heading>
                <Text color="white">
                  Our AI doesn’t just read your prompt — it understands it. By analyzing context, intent, and detail, it enhances your input to deliver the most accurate and visually striking results.
                </Text>
              </Box>
              
              <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                textAlign="center"
              >
                <Icon as={FaImage} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color="white" mb={3}>Style Customization</Heading>
                <Text color="white">
                  Take full creative control. Adjust styles, apply negative prompts, and fine-tune advanced settings to craft the exact image you envision.
                </Text>
              </Box>
              
              <Box
                sx={{
                  '--border-top-width': '0.5px',
                  '--border-right-width': '0.5px',
                  '--border-bottom-width': '0.5px',
                  '--border-left-width': '0.5px',
                  '--border-style': 'dashed',
                  '--border-color': 'rgb(7, 74, 0)',
                  'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                  'borderStyle': 'var(--border-style)',
                  'borderColor': 'var(--border-color)'
                }}
                borderRadius="20px"
                overflow="hidden"
                bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
                p={6}
                transition="all 0.3s ease-in-out"
                boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                  borderColor: 'rgba(32, 230, 13, 0.6)'
                }}
                className="glow-border"
                position="relative"
                textAlign="center"
              >
                <Icon as={FaRocket} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color="white" mb={3}>Ultra-Fast Generation</Heading>
                <Text color="white">
                  No waiting, just creating. Our high-performance AI delivers stunning, high-resolution visuals in seconds — optimized for speed without sacrificing quality.
                </Text>
              </Box>
            </Grid>
          </Box>

          {/* FAQ Section */}
          <Box py={12} id="faq" px={{ base: 2, md: 0 }} overflow="hidden">
            <Heading 
              as="h2" 
              size="xl" 
              mb={4}
              textAlign="center"
              color="white"
              fontWeight="bold"
              px={{ base: 2, md: 0 }}
            >
              Frequently<br/>Asked Questions
            </Heading>
            <Text
              textAlign="center"
              color="rgba(255, 255, 255, 0.8)"
              fontSize="lg"
              mb={10}
              maxW="3xl"
              mx="auto"
              px={{ base: 4, md: 0 }}
            >
              Find answers to common questions about Ignita and the $XGCL ecosystem
            </Text>
            
            <VStack 
              spacing={4} 
              maxW={{ base: "100%", md: "4xl" }} 
              mx="auto" 
              px={{ base: 2, md: 0 }}
              overflow="hidden"
              w="100%"
            >
              {faqData.map((faq) => (
                <FAQItem
                  key={faq.id}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === faq.id}
                  onToggle={() => handleFAQToggle(faq.id)}
                />
              ))}
            </VStack>
          </Box>

          {/* Additional space at the bottom */}
          <Box py={6}></Box>
        </VStack>
      </Container>
    </Box>
  );
}

export default Home;